import { showGeoBlockModalAtom, showSignUpModalAtom } from '@/atoms/chatAtoms';
import { useTrackSignUpWallViewed } from '@/hooks/useTrackSignUpWallViewed';
import { isAllowedOrigin, type WidgetMessage } from '@/lib/embed.utils';
import { EvidenceSignUpWall } from '@/models/analytics/features/evidence';
import { setUtmParamsFromExternal } from '@/utils/utmTracking';
import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';

interface UseEmbedMessagingOptions {
  /**
   * When true, login/signup clicks inside an iframe post `heidi:login` /
   * `heidi:signup` messages to the parent and prevent default navigation.
   * When false (default), those clicks are silently ignored so the host page
   * handles auth itself.
   */
  notifyParentOnAuthActions?: boolean;
}

export function useEmbedMessaging({
  notifyParentOnAuthActions = false,
}: UseEmbedMessagingOptions = {}) {
  const [showSignUpModal, setShowSignUpModal] = useAtom(showSignUpModalAtom);
  const showGeoBlockModal = useAtomValue(showGeoBlockModalAtom);
  const trackSignUpWallViewed = useTrackSignUpWallViewed();

  const postToParent = useCallback((message: WidgetMessage) => {
    try {
      if (window.parent !== window) {
        window.parent.postMessage(message, '*');
      }
    } catch (e) {
      console.error('[Embed] Failed to post message to parent:', e);
    }
  }, []);

  // Notify parent when the geo-block modal opens or closes.
  // On close, skip if the signup modal is still showing — the iframe should remain expanded.
  const prevShowGeoBlockModal = useRef(false);
  useEffect(() => {
    if (!prevShowGeoBlockModal.current && showGeoBlockModal) {
      postToParent({ type: 'heidi:modalOpened' });
    } else if (prevShowGeoBlockModal.current && !showGeoBlockModal && !showSignUpModal) {
      postToParent({ type: 'heidi:modalClosed' });
    }
    prevShowGeoBlockModal.current = showGeoBlockModal;
  }, [showGeoBlockModal, showSignUpModal, postToParent]);

  // Notify parent when the signup/login modal closes (X button, overlay click, Escape, etc.)
  // Skip if the geo-block modal is still showing — the iframe should remain expanded.
  const prevShowSignUpModal = useRef(false);
  useEffect(() => {
    if (prevShowSignUpModal.current && !showSignUpModal && !showGeoBlockModal) {
      postToParent({ type: 'heidi:modalClosed' });
    }
    prevShowSignUpModal.current = showSignUpModal;
  }, [showSignUpModal, showGeoBlockModal, postToParent]);

  const handleMessage = useCallback(
    (event: MessageEvent<unknown>) => {
      // Validate origin for security
      if (!isAllowedOrigin(event.origin)) {
        return;
      }

      const data = event.data;

      // Type guard for parent messages
      if (typeof data !== 'object' || data === null) return;
      const msg = data as Record<string, unknown>;
      if (typeof msg.type !== 'string') return;

      switch (msg.type) {
        case 'heidi:send':
          // Parent wants to send a message - this would need to trigger the chat input
          console.log('[Embed] Received send command:', msg.message);
          break;
        case 'heidi:setTheme':
          // TODO: call useAppearanceMode.setMode() to apply embed-provided theme
          console.log('[Embed] Received theme:', msg.theme);
          break;
        case 'heidi:openSignupModal':
        case 'heidi:openLoginModal':
          trackSignUpWallViewed(EvidenceSignUpWall.ExternalTriggerWall);
          setShowSignUpModal(true);
          postToParent({ type: 'heidi:modalOpened' });
          break;
        case 'heidi:tracking':
          if (msg.currentParams && typeof msg.currentParams === 'object') {
            setUtmParamsFromExternal(msg.currentParams as Record<string, unknown>);
          }
          break;
      }
    },
    [setShowSignUpModal, postToParent, trackSignUpWallViewed]
  );

  // Listen for messages from parent
  useEffect(() => {
    const listener = (e: MessageEvent<unknown>) => handleMessage(e);
    window.addEventListener('message', listener);
    return () => window.removeEventListener('message', listener);
  }, [handleMessage]);

  // Intercept navigation that should redirect the parent page (login, signup, external links)
  useEffect(() => {
    if (window.parent === window) return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest('a');
      const button = target.closest('button');

      const href = link?.getAttribute('href') || '';
      const buttonText = button?.textContent?.toLowerCase() || '';
      const linkText = link?.textContent?.toLowerCase() || '';

      const loginPatterns = ['/login', '/signin', '/auth', 'login', 'sign in', 'log in'];
      const signupPatterns = [
        '/signup',
        '/register',
        '/join',
        'sign up',
        'register',
        'create account',
        'get started',
      ];

      const isLoginAction = loginPatterns.some(
        (p) => href.includes(p) || buttonText.includes(p) || linkText.includes(p)
      );
      const isSignupAction = signupPatterns.some(
        (p) => href.includes(p) || buttonText.includes(p) || linkText.includes(p)
      );

      if (isLoginAction) {
        if (notifyParentOnAuthActions) {
          e.preventDefault();
          e.stopPropagation();
          postToParent({ type: 'heidi:login' });
          if (
            !buttonText &&
            href &&
            !href.startsWith('#') &&
            !href.startsWith('javascript:')
          ) {
            const fullUrl = href.startsWith('http')
              ? href
              : `${window.location.origin}${href}`;
            postToParent({ type: 'heidi:redirect', url: fullUrl, target: '_top' });
          }
        }
        return;
      }

      if (isSignupAction) {
        if (notifyParentOnAuthActions) {
          e.preventDefault();
          e.stopPropagation();
          postToParent({ type: 'heidi:signup' });
          if (
            !buttonText &&
            href &&
            !href.startsWith('#') &&
            !href.startsWith('javascript:')
          ) {
            const fullUrl = href.startsWith('http')
              ? href
              : `${window.location.origin}${href}`;
            postToParent({ type: 'heidi:redirect', url: fullUrl, target: '_top' });
          }
        }
        return;
      }

      // For external links, open in parent
      if (
        !buttonText &&
        link &&
        href &&
        !href.startsWith('#') &&
        !href.startsWith('javascript:')
      ) {
        const isExternal =
          href.startsWith('http') && !href.includes(window.location.host);
        const opensInNewTab = link.getAttribute('target') === '_blank';

        if (isExternal || opensInNewTab) {
          e.preventDefault();
          postToParent({
            type: 'heidi:redirect',
            url: href,
            target: opensInNewTab ? '_blank' : '_top',
          });
        }
      }
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [postToParent, notifyParentOnAuthActions]);

  return { postToParent, setShowSignUpModal };
}
